import type { PairingPriority } from '../types/playEvent';

/** Operator-configurable pairing priorities (skill_balance is legacy-only). */
export const CONFIGURABLE_PAIRING_PRIORITIES = [
  'mixed_gender',
  'player_rating_balance',
  'age_balance',
  'minimize_repeat_partners',
] as const satisfies readonly PairingPriority[];

export type ConfigurablePairingPriority = (typeof CONFIGURABLE_PAIRING_PRIORITIES)[number];

export const DEFAULT_PAIRING_PRIORITIES: PairingPriority[] = [...CONFIGURABLE_PAIRING_PRIORITIES];

export function isConfigurablePairingPriority(
  priority: PairingPriority
): priority is ConfigurablePairingPriority {
  return (CONFIGURABLE_PAIRING_PRIORITIES as readonly PairingPriority[]).includes(priority);
}

/** Drop unknown values, map legacy skill_balance, dedupe; preserve enabled order only. */
export function normalizePairingPriorities(
  priorities: PairingPriority[] | null | undefined
): PairingPriority[] {
  const seen = new Set<PairingPriority>();
  const result: PairingPriority[] = [];

  for (const raw of priorities ?? []) {
    const priority: PairingPriority =
      raw === 'skill_balance' ? 'player_rating_balance' : raw;
    if (!isConfigurablePairingPriority(priority) || seen.has(priority)) {
      continue;
    }
    seen.add(priority);
    result.push(priority);
  }

  return result.length > 0 ? result : [...DEFAULT_PAIRING_PRIORITIES];
}

export function buildConfigurablePairingOrder(
  priorities: PairingPriority[] | null | undefined
): ConfigurablePairingPriority[] {
  const enabled = normalizePairingPriorities(priorities).filter(isConfigurablePairingPriority);
  const disabled = CONFIGURABLE_PAIRING_PRIORITIES.filter((priority) => !enabled.includes(priority));
  return [...enabled, ...disabled];
}

export function pairingPrioritiesFromOrder(
  order: readonly ConfigurablePairingPriority[],
  enabled: ReadonlySet<ConfigurablePairingPriority>
): PairingPriority[] {
  const next = order.filter((priority) => enabled.has(priority));
  return next.length > 0 ? next : [...DEFAULT_PAIRING_PRIORITIES];
}
